import { Component, OnInit, computed, inject, signal } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom } from 'rxjs';

import { ImageCheckService } from '../../../../services/image-check/image-check.service';
import { TournamentService } from '../../../../services/tournament/tournament.service';
import { LoggingService } from '../../../../services/shared/logging/logging.service';
import { DialogService } from '../../../../services/shared/dialog/dialog.service';
import { TableSortingService, SortConfig } from '../../../../services/shared/table-sorting/table-sorting.service';
import { TablePaginationService } from '../../../../services/shared/table-pagination/table-pagination.service';
import { AlertContainerComponent } from '../../../../components/shared/alert-container/alert-container.component';
import { SortableHeaderComponent } from '../../../../components/shared/sortable-header/sortable-header.component';
import { TablePaginationComponent, PageChangeEvent } from '../../../../components/shared/table-pagination/table-pagination.component';

import { ImageCheckPlayerResult, ImageCheckOperationSummary } from '../../../../models/image-check.models';
import { Tournament } from '../../../../models/tournament';
import { TournamentPlayer } from '../../../../models/tournamentPlayer';

/**
 * Component for checking player headshot images
 * Validates that images exist at specified endpoint URLs
 */
@Component({
  selector: 'app-image-check',
  standalone: true,
  imports: [
    FormsModule,
    AlertContainerComponent,
    SortableHeaderComponent,
    TablePaginationComponent
],
  templateUrl: './image-check.component.html',
  styleUrls: ['./image-check.component.scss']
})
export class ImageCheckComponent implements OnInit {
  private imageCheckService = inject(ImageCheckService);
  private tournamentService = inject(TournamentService);
  private loggingService = inject(LoggingService);
  private toastr = inject(ToastrService);
  private dialogService = inject(DialogService);
  private tableSortingService = inject(TableSortingService);
  private tablePaginationService = inject(TablePaginationService);

  // Tournament selection
  readonly selectedTournament = signal<Tournament | null>(null);
  readonly availableTournaments = signal<Tournament[]>([]);
  readonly tournamentPlayers = signal<TournamentPlayer[]>([]);

  // Player results
  readonly players = signal<ImageCheckPlayerResult[]>([]);
  readonly operationState = this.imageCheckService.operationState$;
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);

  // Configuration
  readonly endpointUrl = signal<string>('');
  readonly endpointOverride = signal<string>('');

  // Progress tracking
  readonly checkedCount = signal(0);
  readonly totalCount = computed(() => this.players().length);
  readonly progress = computed(() => {
    const total = this.totalCount();
    return total ? Math.round((this.checkedCount() / total) * 100) : 0;
  });
  readonly failedCount = computed(() => 
    this.players().filter(p => p.status === 'failed').length
  );
  readonly successCount = computed(() => 
    this.players().filter(p => p.status === 'success').length
  );

  // UI state
  readonly searchTerm = signal('');
  readonly showOnlyFailed = signal(false);
  readonly sortColumn = signal<'playerId' | 'firstName' | 'lastName' | 'status'>('playerId');
  readonly sortDirection = signal<'asc' | 'desc'>('asc');
  readonly pageIndex = signal(0);
  readonly pageSize = signal(10);

  // Computed: filtered and sorted players
  readonly filteredPlayers = computed(() => {
    let results = this.players();
    const search = this.searchTerm().toLowerCase();
    
    // Filter by failed status if toggle is on
    if (this.showOnlyFailed()) {
      results = results.filter(p => p.status === 'failed');
    }
    
    if (search) {
      results = results.filter(p => 
        p.playerId.toLowerCase().includes(search) ||
        p.firstName.toLowerCase().includes(search) ||
        p.lastName.toLowerCase().includes(search)
      );
    }

    return this.tableSortingService.sort(
      results,
      this.sortColumn(),
      this.sortDirection()
    );
  });

  // Computed: paginated players for display
  readonly paginatedPlayers = computed(() => {
    const filtered = this.filteredPlayers();
    const start = this.pageIndex() * this.pageSize();
    return filtered.slice(start, start + this.pageSize());
  });

  // Computed: pagination result for table-pagination component
  readonly paginationResult = computed(() => 
    this.tablePaginationService.paginate(
      this.filteredPlayers(),
      this.pageIndex() + 1, // Convert to 1-based
      this.pageSize()
    )
  );

  // Button states
  readonly canRun = computed(() => {
    const state = this.operationState();
    return (state === 'idle' || state === 'completed') && this.totalCount() > 0;
  });
  readonly canPause = computed(() => this.operationState() === 'running');
  readonly canResume = computed(() => this.operationState() === 'paused');
  readonly canStop = computed(() => 
    this.operationState() === 'running' || this.operationState() === 'paused'
  );
  readonly canRetryFailed = computed(() => {
    const state = this.operationState();
    return (state === 'idle' || state === 'completed') && this.failedCount() > 0;
  });
  readonly canCopyResults = computed(() => this.failedCount() > 0);

  ngOnInit(): void {
    this.initializeComponent();
  }

  /**
   * Initialize component - load tournaments and endpoint settings
   */
  private async initializeComponent(): Promise<void> {
    this.loggingService.info('ImageCheck', 'Initializing Image Check component');
    
    // Load endpoint settings
    this.endpointUrl.set(this.imageCheckService.getEndpointUrl());
    
    // Load tournaments
    try {
      this.loading.set(true);
      const tournaments = await firstValueFrom(this.tournamentService.getAll());
      this.availableTournaments.set(tournaments);
      
      // Set selected tournament from service if available
      const currentTournament = this.tournamentService.selectedTournament;
      if (currentTournament) {
        await this.onTournamentSelected(currentTournament);
      }
    } catch (error) {
      this.loggingService.error('ImageCheck', 'Failed to load tournaments', error);
      this.error.set('Failed to load tournaments');
      this.toastr.error('Failed to load tournaments', 'Error');
    } finally {
      this.loading.set(false);
    }
  }

  /**
   * Handle tournament dropdown change event
   */
  handleTournamentChange(tournamentId: string): void {
    const tournament = tournamentId ? 
      this.availableTournaments().find(t => t.tournamentId === tournamentId) ?? null : 
      null;
    this.onTournamentSelected(tournament);
  }

  /**
   * Handle tournament selection
   */
  async onTournamentSelected(tournament: Tournament | null): Promise<void> {
    if (!tournament) {
      this.selectedTournament.set(null);
      this.tournamentPlayers.set([]);
      this.players.set([]);
      return;
    }

    this.selectedTournament.set(tournament);
    this.loggingService.info('ImageCheck', 'Tournament selected', { 
      tournamentId: tournament.tournamentId, 
      name: tournament.name 
    });

    // Load players for the tournament
    try {
      this.loading.set(true);
      const players = await firstValueFrom(
        this.tournamentService.getTournamentPlayers(tournament.tournamentId!)
      );
      this.tournamentPlayers.set(players);
      this.loggingService.info('ImageCheck', `Loaded ${players.length} players for tournament`);
      
      // Reset results and initialize player results with pending status
      this.imageCheckService.reset();
      const initialResults: ImageCheckPlayerResult[] = players.map(p => ({
        playerId: p.lpgaId?.toString() ?? p.playerId ?? '',
        firstName: p.playerFirstName ?? '',
        lastName: p.playerLastName ?? '',
        status: 'pending',
        attempts: 0
      }));
      this.players.set(initialResults);
      this.checkedCount.set(0);
    } catch (error) {
      this.loggingService.error('ImageCheck', 'Failed to load players', error);
      this.error.set('Failed to load players');
      this.toastr.error('Failed to load tournament players', 'Error');
    } finally {
      this.loading.set(false);
    }
  }

  /**
   * Start image check operation
   */
  async startImageCheck(): Promise<void> {
    const tournament = this.selectedTournament();
    const players = this.tournamentPlayers();

    // Validation
    if (!tournament) {
      this.toastr.error('Please select a tournament first', 'No Tournament');
      return;
    }

    if (players.length === 0) {
      this.toastr.error('No players found in selected tournament', 'No Players');
      return;
    }

    const endpoint = this.endpointOverride() || this.endpointUrl();
    
    // Validate URL format
    if (!endpoint) {
      this.toastr.error('Endpoint URL is required', 'Configuration Error');
      return;
    }
    
    if (!this.isValidUrl(endpoint)) {
      this.toastr.error('Invalid endpoint URL format. Please enter a valid URL (e.g., https://images.example.com/players)', 'Invalid URL');
      return;
    }

    // Validate endpoint using NoID.jpg
    this.toastr.info('Validating endpoint with NoID.jpg...');
    const validation = await this.imageCheckService.validateEndpoint(endpoint);
    
    if (!validation.isValid) {
      const confirmed = await this.dialogService.confirm(
        `Endpoint validation failed: ${validation.error || 'NoID.jpg not found'}\n\nEndpoint: ${endpoint}\n\nThis may be due to:\n• Invalid URL\n• NoID.jpg missing from endpoint\n• Network connectivity issues\n\nDo you want to continue anyway?`,
        { title: 'Endpoint Validation Failed', confirmLabel: 'Continue Anyway', cancelLabel: 'Cancel' }
      );
      
      if (!confirmed) {
        return;
      }
    } else {
      this.toastr.success('Endpoint validated successfully');
    }

    // Confirm for large tournaments
    if (players.length >= 100) {
      const confirmed = await this.dialogService.confirm(
        `This will check images for ${players.length} players. Continue?`,
        { title: 'Large Tournament Check' }
      );
      if (!confirmed) {
        return;
      }
    }

    // Update endpoint if override is set
    if (this.endpointOverride()) {
      this.imageCheckService.setEndpointUrl(this.endpointOverride());
      this.endpointUrl.set(this.endpointOverride());
    }

    this.loggingService.info('ImageCheck', 'Starting image check', {
      tournament: tournament.name,
      playerCount: players.length,
      endpoint
    });

    this.checkedCount.set(0);

    try {
      const summary = await this.imageCheckService.checkImages(
        players.map(p => ({
          id: p.lpgaId?.toString() ?? p.playerId!,
          firstName: p.playerFirstName!,
          lastName: p.playerLastName!
        })),
        endpoint,
        (result) => {
          // Progress callback - update count
          this.checkedCount.update(count => count + 1);
          
          // Update players array
          const currentPlayers = this.imageCheckService.playerResults$();
          this.players.set([...currentPlayers]);
        }
      );

      // Show completion dialog
      await this.showCompletionDialog(summary);
    } catch (error) {
      this.loggingService.error('ImageCheck', 'Image check failed', error);
      this.toastr.error('Image check operation failed', 'Error');
    }
  }

  /**
   * Pause the current operation
   */
  pauseImageCheck(): void {
    this.imageCheckService.pause();
    this.loggingService.info('ImageCheck', 'Operation paused by user');
  }

  /**
   * Resume a paused operation
   */
  resumeImageCheck(): void {
    this.imageCheckService.resume();
    this.loggingService.info('ImageCheck', 'Operation resumed by user');
  }

  /**
   * Stop the current operation
   */
  stopImageCheck(): void {
    this.imageCheckService.stop();
    this.loggingService.info('ImageCheck', 'Operation stopped by user');
  }

  /**
   * Retry failed players
   */
  async retryFailed(): Promise<void> {
    const failedPlayers = this.players().filter(p => p.status === 'failed');
    
    if (failedPlayers.length === 0) {
      return;
    }

    const endpoint = this.endpointUrl();
    this.loggingService.info('ImageCheck', `Retrying ${failedPlayers.length} failed players`);

    this.checkedCount.set(this.successCount());

    try {
      const summary = await this.imageCheckService.checkImages(
        failedPlayers.map(p => ({
          id: p.playerId,
          firstName: p.firstName,
          lastName: p.lastName
        })),
        endpoint,
        (result) => {
          this.checkedCount.update(count => count + 1);
          const currentPlayers = this.imageCheckService.playerResults$();
          this.players.set([...currentPlayers]);
        }
      );

      this.toastr.success(
        `Retry complete: ${summary.successCount} passed, ${summary.failedCount} failed`,
        'Retry Complete'
      );
    } catch (error) {
      this.loggingService.error('ImageCheck', 'Retry failed', error);
      this.toastr.error('Retry operation failed', 'Error');
    }
  }

  /**
   * Copy failed results to clipboard
   */
  async copyResultsToClipboard(): Promise<void> {
    const tournament = this.selectedTournament();
    if (!tournament) {
      return;
    }

    // Only copy failed results
    const results = this.players().filter(p => p.status === 'failed');
    
    if (results.length === 0) {
      this.toastr.info('No failed results to copy', 'Info');
      return;
    }

    const text = this.imageCheckService.formatClipboardExport(
      results,
      tournament.name || 'Unknown Tournament'
    );

    try {
      await navigator.clipboard.writeText(text);
      this.toastr.success(`Copied ${results.length} failed players to clipboard`, 'Copied');
      this.loggingService.info('ImageCheck', 'Failed results copied to clipboard', { count: results.length });
    } catch (error) {
      this.loggingService.error('ImageCheck', 'Failed to copy to clipboard', error);
      this.toastr.error('Failed to copy results', 'Error');
    }
  }

  /**
   * Update endpoint URL override
   */
  updateEndpointUrl(url: string): void {
    this.endpointOverride.set(url);
  }

  /**
   * Clear endpoint override
   */
  clearEndpointOverride(): void {
    this.endpointOverride.set('');
  }

  /**
   * Handle sort change
   */
  onSort(config: SortConfig): void {
    this.sortColumn.set(config.key as 'playerId' | 'firstName' | 'lastName' | 'status');
    this.sortDirection.set(config.direction);
    this.pageIndex.set(0); // Reset to first page
  }

  /**
   * Handle page change
   */
  onPageChange(event: PageChangeEvent): void {
    this.pageIndex.set(event.page - 1); // Convert to 0-based
    // pageSize could also be updated if it changes
    if (event.pageSize !== this.pageSize()) {
      this.pageSize.set(event.pageSize);
    }
  }

  /**
   * Handle page size dropdown change
   */
  onPageSizeChange(value: string): void {
    const newSize = parseInt(value, 10);
    if (newSize && newSize !== this.pageSize()) {
      this.pageSize.set(newSize);
      this.pageIndex.set(0); // Reset to first page
    }
  }

  /**
   * Get status icon
   */
  getStatusIcon(status: string): string {
    switch (status) {
      case 'pending': return '◯';
      case 'checking': return '⏳';
      case 'success': return '✓';
      case 'failed': return '✗';
      default: return '';
    }
  }

  /**
   * Get status color class
   */
  getStatusColorClass(status: string): string {
    switch (status) {
      case 'pending': return 'text-muted';
      case 'checking': return 'text-info';
      case 'success': return 'text-success';
      case 'failed': return 'text-danger';
      default: return '';
    }
  }

  /**
   * Get status display text
   */
  getStatusText(result: ImageCheckPlayerResult): string {
    if (result.status === 'success') {
      return 'Success';
    }
    if (result.status === 'failed') {
      if (result.responseCode && result.responseCode !== 0) {
        return `Failed (${result.responseCode})`;
      }
      return result.errorMessage || 'Failed';
    }
    return result.status.charAt(0).toUpperCase() + result.status.slice(1);
  }

  /**
   * Show completion status dialog
   */
  private async showCompletionDialog(summary: ImageCheckOperationSummary): Promise<void> {
    const durationSeconds = Math.round(summary.durationMs / 1000);
    const minutes = Math.floor(durationSeconds / 60);
    const seconds = durationSeconds % 60;
    const durationText = minutes > 0 
      ? `${minutes} min ${seconds} sec`
      : `${seconds} sec`;

    const message = `
Tournament: ${this.selectedTournament()?.name || 'Unknown'}

Total Players Checked: ${summary.totalPlayers}
────────────────────────────────
✓ Passed (200): ${summary.successCount}
✗ Failed: ${summary.failedCount}

Duration: ${durationText}
    `;

    await this.dialogService.confirm(message, {
      title: '✓ Image Check Complete',
      confirmLabel: 'OK',
      hideCancelButton: true
    });

    // Use color-coded toastr based on results
    const resultMessage = `${summary.successCount} passed, ${summary.failedCount} failed`;
    
    if (summary.failedCount === 0) {
      // GREEN: All passed
      this.toastr.success(resultMessage, 'Image Check Complete');
    } else if (summary.successCount === 0) {
      // RED: All failed
      this.toastr.error(resultMessage, 'Image Check Complete');
    } else {
      // WARNING: Some passed, some failed
      this.toastr.warning(resultMessage, 'Image Check Complete');
    }
  }

  /**
   * Validate URL format
   */
  private isValidUrl(url: string): boolean {
    try {
      const parsedUrl = new URL(url);
      // Ensure it's http or https
      return parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:';
    } catch {
      return false;
    }
  }
}
